<template>
  <ion-page>
    <ion-tabs>
      <ion-router-outlet />
      <!-- No `absolute` — keeps tab-bar in Ionic's `slot="bottom"` flex-flow
           so `<ion-footer>` on child pages sits above it automatically. -->
      <ion-tab-bar
        slot="bottom"
        :class="tabBarClasses"
        :style="tabBarStyles"
        class="transition-transform duration-300 ease-in-out"
      >
        <ion-tab-button
          v-for="(tab, index) in nagivationConfig"
          :key="index"
          :tab="tab.icon"
          :tag="tab.tab"
          @click="tab.onClick"
        >
          <AppIcon
            :icon="tab.icon"
            :class="[
              ...tab.iconClasses,
              tab.active
                ? [AppStore.isOffline ? 'text-fg-yellow' : 'text-fg-blue']
                : [],
            ]"
            :stroke-width="tab.active ? '2px' : '1.6px'"
            size="custom"
            class="transition-[stroke-width] duration-200"
          />
        </ion-tab-button>
      </ion-tab-bar>
    </ion-tabs>
  </ion-page>
</template>

<script setup lang="ts">
import { computed, watch, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from '~/stores/app'
import { useAuthStore } from '~/stores/auth'
import {
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonPage,
  IonRouterOutlet,
  useIonRouter,
} from '@ionic/vue'
import AppIcon from '@restify/packages/design-system/low-level/AppIcon.vue'
import { useInjectAppUIState } from '@restify/packages/composables/useAppUiState'
import { STAFF_ROLE_VALUES_HUMAN } from '@restify/packages/types/typebox/staff-roles'

const ionRouter = inject('navManager')
const router = useIonRouter()
const vueRouter = useRouter()
const route = useRoute()
const AppStore = useAppStore()
const AuthStore = useAuthStore()
const { appUiTabBarIsVisible } = useInjectAppUIState()

const nagivationConfig = computed(() => {
  const storefront = AppStore.selectedStorefront

  if (!storefront) return []

  return [
    ...(storefront.inHouseService?.enabled &&
    storefront.inHouseService?.setup?.map
      ? [
          {
            to: {
              name: 'map',
            },
            icon: 'map',
            tab: 'map',
            iconClasses: ['w-[28px]', 'h-[28px]'],
          },
        ]
      : []),
    ...(storefront.inHouseService?.enabled &&
    storefront.inHouseService?.setup?.tabs
      ? [
          {
            to: {
              name: 'tabs',
            },
            icon: 'type',
            tab: 'tabs',
            iconClasses: ['w-[28px]', 'h-[28px]'],
          },
        ]
      : []),
    {
      to: {
        name: 'in-house',
      },
      icon: 'list',
      tab: 'in-house',
      iconClasses: ['w-[28px]', 'h-[28px]'],
    },
    ...((STAFF_ROLE_VALUES_HUMAN as string[]).includes(
      AuthStore.cachedUser?.profile?.role || '',
    )
      ? [
          {
            to: {
              name: 'tasks',
            },
            icon: 'clipboard-check',
            tab: 'tasks',
            iconClasses: ['w-[28px]', 'h-[28px]'],
          },
        ]
      : []),
    ...(['manager', 'superadmin'].includes(
      AuthStore.cachedUser?.profile?.role || '',
    )
      ? [
          {
            to: {
              name: 'procurement',
            },
            icon: 'delivery-truck',
            tab: 'procurement',
            iconClasses: ['w-[28px]', 'h-[28px]'],
          },
        ]
      : []),
    ...(AuthStore.cachedUser?.profile?.role === 'manager'
      ? [
          {
            to: {
              name: 'dashboard',
            },
            icon: 'coins',
            tab: 'dashboard',
            iconClasses: ['w-[28px]', 'h-[28px]'],
          },
        ]
      : []),
    // {
    //   to: {
    //     name: 'scan'
    //   },
    //   icon: 'scan-qr-code',
    //   tab: 'scan',
    //   iconClasses: ['w-[28px]', 'h-[28px]']
    // },
    // {
    //   to: {
    //     name: 'to-go'
    //   },
    //   icon: 'shopping-bag',
    //   tab: 'to-go',
    //   iconClasses: ['w-[30px]', 'h-[30px]']
    // },
    {
      to: {
        name: 'user',
      },
      icon: 'user',
      tab: 'user',
      iconClasses: ['w-[28px]', 'h-[28px]'],
    },
  ].map((tab) => {
    const href = vueRouter.resolve(tab.to).href

    return {
      ...tab,
      href,
      active: route.fullPath.includes(href),
      onClick: () => {
        if (
          route.name === 'dashboard-shift-id' ||
          route.name === 'tasks-id' ||
          route.name === 'procurement-id' ||
          route.name === 'procurement-item' ||
          route.name === 'procurement-new' ||
          route.name === 'user-storefront-select' ||
          route.name === 'user-printer' ||
          route.name === 'user-printer-report'
        ) {
          // @ts-ignore
          router.navigate(href, 'back', 'pop')
        } else {
          // @ts-ignore
          ionRouter.changeTab(tab.tab, href)
        }
      },
    }
  })
})

const tabBarClasses = computed(() => {
  return {
    unauthorized: ['shadow-elevation-1-0-lg'],
    map: ['shadow-elevation-1-0-lg'],
    'in-house': ['shadow-elevation-1-1-lg'],
    tasks: ['shadow-elevation-1-0-lg'],
    'tasks-id': ['shadow-elevation-1-0-lg'],
    procurement: ['shadow-elevation-1-0-lg'],
    'procurement-id': ['shadow-elevation-1-0-lg'],
    'procurement-new': ['shadow-elevation-1-0-lg'],
    'procurement-item': ['shadow-elevation-1-0-lg'],
    scan: ['shadow-elevation-1-0-lg'],
    'to-go': ['shadow-elevation-1-0-lg'],
    dashboard: ['shadow-elevation-1-0-lg'],
    'dashboard-shift-id': ['shadow-elevation-1-0-lg'],
    user: ['shadow-elevation-1-0-lg'],
    'user-storefront-select': ['shadow-elevation-1-0-lg'],
    'user-printer': ['shadow-elevation-1-0-lg'],
    'user-printer-report': ['shadow-elevation-1-0-lg'],
    tabs: ['shadow-elevation-1-1-lg'],
  }[String(route.name)]
})

const tabBarStyles = computed(() => {
  return {
    ...{
      unauthorized: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      map: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'in-house': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      scan: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'to-go': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      tasks: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'tasks-id': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      procurement: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'procurement-id': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'procurement-new': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'procurement-item': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      dashboard: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'dashboard-shift-id': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      user: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'user-storefront-select': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'user-printer': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      'user-printer-report': {
        '--background': 'var(--rf-bg-elevation-1)',
        '--border': '0.5px solid var(--rf-border-elevation-1-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
      tabs: {
        '--background': 'var(--rf-bg-elevation-1-ios)',
        '--border': '0.5px solid var(--rf-border-elevation-1-ios-primary)',
        '--color': 'var(--rf-fg-elevation-1-tertiary)',
      },
    }[String(route.name || 'user')],
    transform: appUiTabBarIsVisible.value ? null : 'translateY(100%)',
  }
})

watch(
  computed(() => route.fullPath),
  () => {
    navigator.serviceWorker.ready.then((registration) => {
      console.log('Trying to update SW')
      registration.update()
    })
  },
)
</script>
